import { useEffect, useState, type ReactNode } from 'react';
import { cn } from '@/lib/core/utils/cn';

/**
 * A detail screen's fixed bottom CTA panel (z-41, over the navbar's z-40) stays put while the
 * `CoveringFooter` scrolls up over it (z-42). Once the footer covers the whole panel, the panel hides
 * and the footer drops below the navbar, revealing it. A page too short to scroll never gets there,
 * so it keeps the panel rather than leaving no way to act on the page.
 *
 * `position: sticky` can't do this here: the global `overflow-x: hidden` on html + body makes body a
 * non-scrolling scroll container, so sticky never engages.
 *
 * Refs are callback-ref state because both elements usually mount only once the page's data loads.
 */
export function useCoveredBottomPanel() {
  const [panelEl, setPanelEl] = useState<HTMLDivElement | null>(null);
  const [footerEl, setFooterEl] = useState<HTMLDivElement | null>(null);
  const [panelHeight, setPanelHeight] = useState(0);
  const [isPanelCovered, setIsPanelCovered] = useState(false);

  useEffect(() => {
    // A panel that renders nothing (no CTAs) must not leave a stale height behind.
    if (!panelEl) {
      setPanelHeight(0);
      return;
    }
    const observer = new ResizeObserver(() => setPanelHeight(panelEl.offsetHeight));
    observer.observe(panelEl);
    return () => observer.disconnect();
  }, [panelEl]);

  useEffect(() => {
    if (!footerEl || !panelHeight) {
      setIsPanelCovered(false);
      return;
    }
    // Shrinks the viewport by the panel's height, so the footer "intersects" exactly when its top
    // has risen past the panel's top edge.
    const observer = new IntersectionObserver(
      ([entry]) => {
        const canScroll = document.documentElement.scrollHeight > window.innerHeight;
        setIsPanelCovered(canScroll && entry.isIntersecting);
      },
      { rootMargin: `0px 0px -${panelHeight}px 0px` }
    );
    observer.observe(footerEl);
    return () => observer.disconnect();
  }, [footerEl, panelHeight]);

  return { panelRef: setPanelEl, footerRef: setFooterEl, panelHeight, isPanelCovered };
}

/** Classes for the fixed panel paired with `CoveringFooter`. */
export function coveredPanelClassName(isPanelCovered: boolean) {
  return cn('fixed inset-x-0 bottom-0 z-41', isPanelCovered && 'invisible');
}

interface CoveringFooterProps {
  footerRef: (el: HTMLDivElement | null) => void;
  panelHeight: number;
  isPanelCovered: boolean;
  className?: string;
  children?: ReactNode;
}

/**
 * Its min-height guarantees it can cover the panel fully, and the bottom room keeps its content clear
 * of the navbar pill (h-12 plus its 1.5rem + safe-area offset, mirrored above).
 */
export function CoveringFooter({
  footerRef,
  panelHeight,
  isPanelCovered,
  className,
  children,
}: CoveringFooterProps) {
  return (
    <div
      ref={footerRef}
      className={cn(
        'relative flex shrink-0 flex-col px-4 pt-14 pb-[calc(3rem+3rem+env(safe-area-inset-bottom))]',
        // Only rises over a panel that exists — otherwise it would sit on top of the navbar.
        panelHeight > 0 && !isPanelCovered && 'z-42',
        className
      )}
      style={{ minHeight: panelHeight }}
    >
      {/* Fades the panel out as the footer's top edge slides over it, instead of a hard cut: the top
          padding strip is a transparent → white gradient, solid white from there down. */}
      <div
        aria-hidden="true"
        className="to-jncquoi-neutral-00 pointer-events-none absolute inset-x-0 top-0 h-14 bg-linear-to-b from-transparent"
      />
      <div
        aria-hidden="true"
        className="bg-jncquoi-neutral-00 pointer-events-none absolute inset-x-0 top-14 bottom-0"
      />
      <div className="relative">{children}</div>
    </div>
  );
}
